import { callApi, RequestType } from "../fetch";
import { showToast } from "../toast";
import { answerUnclear, askBookingService, FollowOptions, followRequest, newRequestId, refusalMessage } from "./booking_service";
import { BookingsData } from "./bookings_repository";
import { BookingInterval } from "./ev_bookings";
import { getUser } from "./user_repository";

// Once a person's company has switched over, the rules refuse OnePark's direct cancel of a series day and a
// direct change to one is refused when it's approved, so OnePark changes and cancels series days through the
// booking service, acting for the person.

export type SeriesBooking = Pick<BookingsData, "bookingId" | "multiID" | "userId" | "plate" | "name" | "bookingType" | "timeType">;

// A series made through the booking service stores its days as single bookings, so OnePark's own series
// functions don't manage it.
export const madeByService = (booking: Partial<SeriesBooking>): boolean => Boolean(booking.multiID) && booking.bookingType === "Single";

// Whether this booking is a series day OnePark has to change through the service. Throws when the service
// can't be reached, so the admin is asked to try again rather than sent down a path that will be refused.
export const seriesNeedsService = async (booking: Partial<SeriesBooking>): Promise<boolean> => {
    if (!booking.multiID || !booking.userId) return false;
    const person = await getUser(booking.userId);
    return Boolean(person) && (await askBookingService(person!)).switched;
};

type Prepared = { data?: { tenantId?: string; gateEpoch?: number; expectedRevision?: unknown } };

const nameOf = (booking: Partial<SeriesBooking>) => booking.name || "this person";

const refuse = (booking: Partial<SeriesBooking>, code: string | undefined, fallback: string): false => {
    showToast(refusalMessage(code, nameOf(booking), fallback), { type: "error" });
    return false;
};

// The current version of the day or series, which the change or cancellation must still match.
const prepare = async (route: "bookings/prepare-edit" | "cancellations/prepare", target: Record<string, unknown>, userId: string) => {
    const response = await callApi<Prepared>(`bookingApiV2/${route}`, { ...target, userId }, RequestType.POST, { signedIn: true });
    const { tenantId, gateEpoch, expectedRevision } = response.data?.data ?? {};
    return response.error || !tenantId || typeof gateEpoch !== "number" || !expectedRevision ?
        { refused: response.code ?? "" } : { version: { tenantId, gateEpoch, expectedRevision } };
};

// A change can also move the booking to the other parking type; without one it keeps its own.
type ChangeOptions = FollowOptions & { parkingSpaceType?: "standard" | "ev" };

const change = async (booking: Partial<SeriesBooking>, route: "bookings/edit" | "series/edit", target: Record<string, unknown>,
    intervals: BookingInterval[], { parkingSpaceType, ...options }: ChangeOptions, done: string): Promise<boolean> => {
    const failed = `We couldn't change ${nameOf(booking)}'s booking just now. Please try again.`;
    // A booking with no vehicle on it is entered with the person's access card; the service checks they have one.
    const plate = (booking.plate ?? []).filter((value) => typeof value === "string" && value.trim().length > 0);
    if (intervals.length === 0 || intervals.some((interval) => interval.endTime <= interval.startTime)) {
        showToast("The end time needs to be after the start time.", { type: "warning" });
        return false;
    }
    const prepared = await prepare("bookings/prepare-edit", target, booking.userId!);
    if (!prepared.version) return refuse(booking, prepared.refused, failed);
    const requestId = newRequestId("edit");
    const response = await callApi<{ data?: { status?: string } }>(`bookingApiV2/${route}`, { ...prepared.version, ...target, requestId,
        intervals, plate, timeType: booking.timeType === "FullDay" ? "FullDay" : "Custom", timeEncoding: "sydney-wall-v1",
        ...(parkingSpaceType ? { parkingSpaceType } : {}), userId: booking.userId }, RequestType.POST, { signedIn: true });
    const unsure = answerUnclear(response);
    if (response.error && !unsure) return refuse(booking, response.code, failed);
    const status = await followRequest("booking-operations/status", requestId, booking.userId!,
        unsure ? "pending" : response.data?.data?.status, options);
    if (status === "succeeded") {
        showToast(done, { type: "success" });
        return true;
    }
    if (status === "pending") {
        showToast(`The car park is still confirming the change to ${nameOf(booking)}'s booking. It'll show once it's confirmed.`, { type: "info" });
        return true;
    }
    return refuse(booking, status === "failed" ? "provider_refused" : undefined, failed);
};

const cancel = async (booking: Partial<SeriesBooking>, route: "bookings/cancel" | "series/cancel", target: Record<string, unknown>,
    options: FollowOptions, done: string): Promise<boolean> => {
    const failed = `We couldn't cancel ${nameOf(booking)}'s booking just now. Please try again.`;
    const prepared = await prepare("cancellations/prepare", target, booking.userId!);
    if (!prepared.version) return refuse(booking, prepared.refused, failed);
    const requestId = newRequestId("cancel");
    const response = await callApi(`bookingApiV2/${route}`, { ...prepared.version, ...target, requestId, userId: booking.userId },
        RequestType.POST, { signedIn: true });
    const unsure = answerUnclear(response);
    if (response.error && !unsure) return refuse(booking, response.code, failed);
    const status = unsure ? await followRequest("operations/status", requestId, booking.userId!, "pending", options) : "succeeded";
    if (status !== "succeeded") return refuse(booking, undefined, failed);
    showToast(done, { type: "success" });
    return true;
};

// Changes the times of one day of the series.
export const changeSeriesDay = (booking: Partial<SeriesBooking>, times: BookingInterval, options: FollowOptions = {}) =>
    change(booking, "bookings/edit", { bookingId: booking.bookingId, multiId: booking.multiID }, [times], options,
        `${nameOf(booking)}'s booking is updated.`);

// Moves a booking that isn't part of a series to the other parking type (and any new times), which only the
// service can do once the company has switched over.
export const changeBooking = (booking: Partial<SeriesBooking>, times: BookingInterval, parkingSpaceType: "standard" | "ev",
    options: FollowOptions = {}) =>
    change(booking, "bookings/edit", { bookingId: booking.bookingId }, [times], { ...options, parkingSpaceType },
        `${nameOf(booking)}'s booking is updated.`);

// Changes every day of a series made through the service to the given days, and to the given parking type.
export const changeSeries = (booking: Partial<SeriesBooking>, days: BookingInterval[], options: ChangeOptions = {}) =>
    change(booking, "series/edit", { multiId: booking.multiID }, days, options, `${nameOf(booking)}'s booking series is updated.`);

// Cancels one day of the series; the rest of it stays.
export const cancelSeriesDay = (booking: Partial<SeriesBooking>, options: FollowOptions = {}) =>
    cancel(booking, "bookings/cancel", { bookingId: booking.bookingId, multiId: booking.multiID }, options,
        `${nameOf(booking)}'s booking for that day is cancelled.`);

// Cancels every day of a series made through the service.
export const cancelSeries = (booking: Partial<SeriesBooking>, options: FollowOptions = {}) =>
    cancel(booking, "series/cancel", { multiId: booking.multiID }, options, `${nameOf(booking)}'s booking series is cancelled.`);
